import { useCallback, useEffect, useRef, useState } from "react";

// Same localStorage key the old MailboxDrawer used, so in-flight user drafts
// survive the migration to the new inbox.
const DRAFTS_KEY = "mailbox_email_drafts";
const DRAFTS_EVENT = "inbox-drafts-changed";

function readDrafts(): Record<string, { content: string; updatedAt: number }> {
	try {
		const stored = localStorage.getItem(DRAFTS_KEY);
		return stored ? JSON.parse(stored) : {};
	} catch {
		return {};
	}
}

function isMeaningfulDraft(content: string | undefined): boolean {
	return !!(content && content.trim() && content !== "<p><br></p>");
}

function writeDraft(conversationId: string, content: string): void {
	try {
		const drafts = readDrafts();
		if (isMeaningfulDraft(content)) {
			drafts[conversationId] = { content, updatedAt: Date.now() };
		} else {
			delete drafts[conversationId];
		}
		localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
		window.dispatchEvent(new CustomEvent(DRAFTS_EVENT));
	} catch {
		// Quota errors just lose the draft persistence, not the input.
	}
}

export function getDraft(conversationId: string): string {
	return readDrafts()[conversationId]?.content || "";
}

/** Draft persistence for one conversation's composer (debounced writes). */
export function useDraft(conversationId: string | undefined) {
	const timeoutRef = useRef<number>();

	const saveDraft = useCallback(
		(content: string) => {
			if (!conversationId) return;
			window.clearTimeout(timeoutRef.current);
			timeoutRef.current = window.setTimeout(() => writeDraft(conversationId, content), 500);
		},
		[conversationId]
	);

	const flushDraft = useCallback(
		(content: string) => {
			if (!conversationId) return;
			window.clearTimeout(timeoutRef.current);
			writeDraft(conversationId, content);
		},
		[conversationId]
	);

	const clearDraft = useCallback(() => {
		if (!conversationId) return;
		window.clearTimeout(timeoutRef.current);
		writeDraft(conversationId, "");
	}, [conversationId]);

	useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

	return { getDraft, saveDraft, flushDraft, clearDraft };
}

/** Reactive set of conversation ids with saved drafts (list-row indicators). */
export function useDraftIndicators(): Set<string> {
	const [draftIds, setDraftIds] = useState<Set<string>>(() => {
		const drafts = readDrafts();
		return new Set(Object.keys(drafts).filter((id) => isMeaningfulDraft(drafts[id]?.content)));
	});

	useEffect(() => {
		const refresh = () => {
			const drafts = readDrafts();
			setDraftIds(new Set(Object.keys(drafts).filter((id) => isMeaningfulDraft(drafts[id]?.content))));
		};
		window.addEventListener(DRAFTS_EVENT, refresh);
		window.addEventListener("storage", refresh);
		return () => {
			window.removeEventListener(DRAFTS_EVENT, refresh);
			window.removeEventListener("storage", refresh);
		};
	}, []);

	return draftIds;
}
